import { buildURLforProviderProcedure } from "pages/CreateOrder/utils/helpers";
import { IPayerWithTerms } from "types/payerTerms";
import {
  IProviderProcedureResponse,
  ProcedureProviderSearchBody,
  IGroupedProviderProcedureResponse,
} from "types/ProviderProcedure";

import { api } from ".";

export interface IProviderProcedureRequest {
  payerNameField?: string;
  cptCodeField?: string;
  cptDescription?: string;
  providerName?: string;
  specialty?: string;
  connectedHospital?: string;
  page?: number;
  pageSize?: number;
  accountId?: string;
  limit?: number;
  /** ISO YYYY-MM-DD — optional; backend resolves prices from the contract fee schedule active on this date. */
  feeEffectiveDate?: string;
}

export interface IProviderProcedureComparisonRequest
  extends IProviderProcedureRequest {
  /** Provider id to scope the comparison to a single provider. */
  providerId?: string;
  onlyLowerThanMedicare?: boolean;
  volume?: number;
  volumeMatchMode?: string;
  contractFeeScheduleIds?: string[];
  /** Multi exact-match CPT filter — repeated query params. */
  cptCodes?: string[];
  /**
   * Multi exact-match service line filter — repeated query params.
   * Values must come from GET /procedures/service-lines (case-sensitive).
   */
  serviceLines?: string[];
}

const providerProcedureQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getProviderProcedure: builder.query<
      IProviderProcedureResponse,
      IProviderProcedureRequest
    >({
      query: ({
        page,
        pageSize,
        payerNameField,
        cptCodeField,
        cptDescription,
        providerName,
        specialty,
        connectedHospital,
        feeEffectiveDate,
      }) => {
        return buildURLforProviderProcedure({
          baseUrl: "provider_procedures",
          payerNameField,
          cptCodeField,
          cptDescription,
          providerName,
          specialty,
          connectedHospital,
          page,
          pageSize,
          feeEffectiveDate,
        });
      },
    }),
    getAllProviderProcedureComparison: builder.query<
      IGroupedProviderProcedureResponse,
      IProviderProcedureComparisonRequest
    >({
      query: ({
        accountId,
        page,
        pageSize,
        payerNameField,
        cptDescription,
        providerName,
        specialty,
        providerId,
        onlyLowerThanMedicare,
        volume,
        volumeMatchMode,
        contractFeeScheduleIds,
        cptCodes,
        serviceLines,
      }) => {
        return buildURLforProviderProcedure({
          baseUrl: `provider_procedures/contract-management/${accountId}`,
          payerNameField,
          cptDescription,
          providerName,
          specialty,
          providerId,
          page,
          pageSize,
          onlyLowerThanMedicare,
          volume,
          volumeMatchMode,
          contractFeeScheduleIds,
          cptCodes,
          serviceLines,
          defaultPagination: false,
        });
      },
    }),
    getContractManagementAvailableFilters: builder.query<
      { cptCodes: string[]; serviceLines: string[] },
      { accountId: string; providerId?: string }
    >({
      query: ({ accountId, providerId }) => {
        const search = providerId
          ? `?providerId=${encodeURIComponent(providerId)}`
          : "";
        return {
          url: `provider_procedures/contract-management/${accountId}/available-filters${search}`,
          method: "GET",
        };
      },
      transformResponse: (response: {
        data: { cptCodes: string[]; serviceLines: string[] };
      }) => response.data ?? { cptCodes: [], serviceLines: [] },
    }),
    getPayerTermsForAccount: builder.query<
      IPayerWithTerms[],
      { accountId: string; specialty?: string; providerId?: string }
    >({
      query: ({ accountId, specialty, providerId }) => {
        // providerId takes precedence over specialty (matches the backend scope).
        let search = "";
        if (providerId) {
          search = `?providerId=${encodeURIComponent(providerId)}`;
        } else if (specialty) {
          search = `?specialty=${encodeURIComponent(specialty)}`;
        }
        return {
          url: `provider_procedures/contract-management/${accountId}/payer-terms${search}`,
          method: "GET",
        };
      },
      transformResponse: (response: { data: IPayerWithTerms[] }) =>
        response.data,
    }),
    getTop10ProviderProcedureComparison: builder.query<
      {
        data: {
          enriched: {
            procedureId: number;
            cptCode: string;
            description: string;
            orderCount: number;
            prices?: {
              payerId: number;
              payerName?: string;
              priceInCents: number;
              contractFeeScheduleEffectiveDate?: string | null;
            }[];
          }[];
          overallVarianceAverages: {
            highestVsLowestCommercialAvg: number;
            highestVsMedicareAvg: number;
            highestVsMedicaidAvg: number;
          };
        };
      },
      { accountId: string; providerId?: string; limit?: number }
    >({
      query: ({ accountId, providerId, limit }) => {
        const parts: string[] = [];
        if (providerId) {
          parts.push(`providerId=${encodeURIComponent(providerId)}`);
        }
        if (limit !== undefined) {
          parts.push(`limit=${limit}`);
        }
        const query = parts.length ? `?${parts.join("&")}` : "";

        return {
          url: `provider_procedures/contract-management/top10/${accountId}${query}`,
          method: "GET",
        };
      },
    }),
    getClearProviderProcedureByProvider: builder.mutation<
      IProviderProcedureResponse,
      { providerId: string }
    >({
      query: ({ providerId }) => ({
        url: `provider_procedures/provider/${providerId}`,
        method: "POST",
      }),
    }),
    deleteProviderProcedure: builder.mutation<any, number>({
      query: (id) => ({
        url: `provider_procedures/${id}`,
        method: "DELETE",
      }),
    }),
    deleteAllProviderProcedure: builder.mutation<any, any>({
      query: (filters) => ({
        url: `provider_procedures/`,
        method: "DELETE",
        body: {
          ...filters,
        },
      }),
    }),
    updateProviderProcedurePrice: builder.mutation<
      any,
      {
        id: number;
        priceInCents?: number;
        implantCostInCents?: number | null;
        toBeEdited?: boolean;
        requiresAbn?: boolean;
        preventive?: boolean;
        drgAverageLengthOfStay?: number | null;
        drgLosOutlierThreshold?: number | null;
        drgOutlierMultiplier?: number | null;
      }
    >({
      query: ({
        id,
        priceInCents,
        implantCostInCents,
        toBeEdited,
        requiresAbn,
        preventive,
        drgAverageLengthOfStay,
        drgLosOutlierThreshold,
        drgOutlierMultiplier,
      }) => ({
        url: `provider_procedures/${id}`,
        method: "PUT",
        body: {
          priceInCents,
          implantCostInCents,
          toBeEdited,
          requiresAbn,
          preventive,
          drgAverageLengthOfStay,
          drgLosOutlierThreshold,
          drgOutlierMultiplier,
        },
      }),
    }),
    searchProviderProcedure: builder.mutation<
      { data: IProviderProcedureResponse },
      ProcedureProviderSearchBody
    >({
      query: ({ accountId, cptCode, payerName, dateOfService }) => ({
        url: `provider_procedures/search`,
        method: "POST",
        body: {
          accountId,
          cptCode,
          payerName,
          ...(dateOfService ? { dateOfService } : {}),
        },
      }),
    }),
    getCptRecommendations: builder.mutation<
      {
        data: {
          recommendations: {
            cptCode: string;
            cptDescription: string;
            occurrenceRate: number;
            pairedWith: string[];
            existsForPayer: boolean;
          }[];
        };
        message: string;
      },
      { accountId: string; includedCptCodes: string[]; payerName?: string }
    >({
      query: ({ accountId, includedCptCodes, payerName }) => ({
        url: `provider_procedures/cpt-recommendations`,
        method: "POST",
        body: {
          accountId,
          includedCptCodes,
          payerName,
        },
      }),
    }),
  }),
});

export const {
  useGetProviderProcedureQuery,
  useGetAllProviderProcedureComparisonQuery,
  useLazyGetAllProviderProcedureComparisonQuery,
  useGetTop10ProviderProcedureComparisonQuery,
  useGetPayerTermsForAccountQuery,
  useGetContractManagementAvailableFiltersQuery,
  useGetClearProviderProcedureByProviderMutation,
  useDeleteProviderProcedureMutation,
  useDeleteAllProviderProcedureMutation,
  useUpdateProviderProcedurePriceMutation,
  useSearchProviderProcedureMutation,
  useGetCptRecommendationsMutation,
} = providerProcedureQueries;

export default providerProcedureQueries;
